import React, { useState, useEffect } from "react";
import { ACTIVITY_STYLES, AUTO_BADGES, getAutoBadges, ALL_STUDENTS as SHARED_STUDENTS, MATH_GROUPS, ELA_GROUPS } from "./SharedStudentData";
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, RadarChart,
  Radar, PolarGrid, PolarAngleAxis, Cell,
} from "recharts";

// ─────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────
const T = {
  bg:      "#F7F9FC",
  white:   "#FFFFFF",
  navy:    "#0D1B2A",
  ink:     "#1E3A5F",
  blue:    "#1A6EBD",
  sky:     "#3B9EE2",
  teal:    "#00A878",
  mint:    "#E6F8F3",
  amber:   "#F59E0B",
  rose:    "#E03B3B",
  blush:   "#FEF0F0",
  gold:    "#D97706",
  slate:   "#5A7293",
  mist:    "#E8EEF6",
  light:   "#EBF2FC",
  purple:  "#7C3AED",
  lavender:"#EDE9FE",
};

// ─────────────────────────────────────────────────────────────
// GROUPING ENGINE — Auto-analyzes student data → groups
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// TWO-LEVEL SKILL TAXONOMY
// Level 1 = domain (e.g. "Reading Comprehension")
// Level 2 = granular focus skill (e.g. "Main Idea & Key Details")
// TO ADD MORE: add a new key under ELA or Math below.
// ─────────────────────────────────────────────────────────────
const SKILL_TAXONOMY = {
  ELA: {
    "Phonological Awareness": [
      "Rhyming", "Syllable Segmentation", "Onset & Rime",
      "Phoneme Isolation", "Phoneme Blending", "Phoneme Segmentation",
      "Phoneme Deletion & Substitution",
    ],
    "Phonics & Decoding": [
      "Letter-Sound Correspondence", "CVC Words", "Consonant Blends & Digraphs",
      "Long Vowel Patterns", "R-Controlled Vowels", "Multisyllabic Words",
      "Irregular/High-Frequency Words", "Prefixes & Suffixes",
    ],
    "Fluency": [
      "Reading Rate (WCPM)", "Accuracy", "Prosody & Expression",
      "Phrasing & Pausing", "Repeated Reading Practice",
      "Partner Reading", "Oral Reading Fluency Benchmark",
    ],
    "Vocabulary": [
      "Context Clues", "Word Morphology (Roots, Prefixes, Suffixes)",
      "Academic Vocabulary (Tier 2)", "Domain-Specific Vocabulary (Tier 3)",
      "Synonyms & Antonyms", "Figurative Language",
      "Word Relationships & Analogies",
    ],
    "Reading Comprehension": [
      "Main Idea & Key Details", "Compare & Contrast",
      "Cause & Effect", "Problem & Solution",
      "Sequence of Events", "Author's Purpose",
      "Inferencing & Drawing Conclusions", "Text Structure",
      "Point of View", "Theme & Central Message",
      "Summarizing", "Making Connections (Text-to-Self/World/Text)",
      "Questioning & Monitoring Comprehension",
    ],
    "Writing": [
      "Opinion/Argumentative Writing", "Informational/Explanatory Writing",
      "Narrative Writing", "Paragraph Structure",
      "Evidence & Elaboration", "Transitions",
      "Introduction & Conclusion", "Editing & Revising",
      "Extended Response", "Short Constructed Response",
    ],
    "Grammar & Conventions": [
      "Complete Sentences & Fragments", "Subject-Verb Agreement",
      "Nouns, Pronouns, Verbs, Adjectives", "Comma Usage",
      "Capitalization & Punctuation", "Complex & Compound Sentences",
      "Tense Consistency", "Quotation Marks & Dialogue",
    ],
  },
  Math: {
    "Number Sense & Place Value": [
      "Counting & Cardinality", "Comparing Numbers",
      "Place Value (Ones, Tens, Hundreds, Thousands)",
      "Rounding", "Number Lines", "Expanded Form",
      "Composing & Decomposing Numbers",
    ],
    "Addition & Subtraction": [
      "Basic Addition Facts", "Basic Subtraction Facts",
      "Multi-Digit Addition (with/without Regrouping)",
      "Multi-Digit Subtraction (with/without Regrouping)",
      "Mental Math Strategies", "Addition & Subtraction Word Problems",
      "Estimating Sums & Differences",
    ],
    "Multiplication & Division": [
      "Multiplication Facts (0-12)", "Division Facts",
      "Properties of Multiplication (Commutative, Associative, Distributive)",
      "Multi-Digit Multiplication", "Long Division",
      "Multiplication & Division Word Problems",
      "Factors & Multiples", "Prime & Composite Numbers",
    ],
    "Fractions & Decimals": [
      "Understanding Fractions (Equal Groups/Parts)",
      "Equivalent Fractions", "Comparing Fractions",
      "Adding Fractions (Same Denominator)",
      "Subtracting Fractions (Same Denominator)",
      "Adding Fractions (Different Denominators)",
      "Subtracting Fractions (Different Denominators)",
      "Mixed Numbers & Improper Fractions",
      "Multiplying Fractions", "Dividing Fractions",
      "Decimal Place Value", "Comparing Decimals",
      "Adding & Subtracting Decimals",
    ],
    "Algebra & Patterns": [
      "Identifying Patterns", "Input/Output Tables",
      "Variables & Expressions", "One-Step Equations",
      "Two-Step Equations", "Inequalities",
      "Ratios & Proportions", "Functions & Graphing",
      "Order of Operations (PEMDAS)",
    ],
    "Geometry": [
      "2D Shapes & Attributes", "3D Shapes & Attributes",
      "Perimeter", "Area", "Volume",
      "Lines, Angles, & Rays", "Symmetry",
      "Coordinate Plane", "Transformations (Translation, Reflection, Rotation)",
      "Congruence & Similarity",
    ],
    "Measurement & Data": [
      "Telling Time", "Elapsed Time",
      "Measuring Length (Standard & Metric)",
      "Weight & Mass", "Capacity & Volume",
      "Reading Graphs (Bar, Line, Pie)",
      "Creating Graphs & Tables",
      "Mean, Median, Mode, Range",
      "Probability & Statistics",
    ],
  },
};

// Flat list for backward compatibility
const SKILL_DOMAINS = {
  ELA:  Object.keys(SKILL_TAXONOMY.ELA),
  Math: Object.keys(SKILL_TAXONOMY.Math),
};

// Activity color map — each club/program gets its own color + icon
// ─────────────────────────────────────────────────────────────
// BADGE & ACTIVITY REGISTRY
// ─────────────────────────────────────────────────────────────
// TO ADD A NEW BADGE OR ACTIVITY:
//   1. Add a new line below with: "Badge Name": { color:"#HEXCODE", icon:"EMOJI" }
//   2. For AUTO badges (Honor Roll, Perfect Attendance), see AUTO_BADGES below
//   3. For manual activities, add the badge name to a student's activities:[] array
//
// Color ideas: purple #7C3AED · blue #1560BD · teal #0F766E · pink #DB2777
//              red #DC2626 · orange #EA580C · amber #D97706 · green #059669
// ─────────────────────────────────────────────────────────────


const STUDENTS = [
  { id:"S01", name:"Alex M.",    grade:6, iready:445, growth:-2, sim:43, att:86.7, nys_prior:1, ell:false, iep:true,
    skill_gaps:["Reading Comprehension","Vocabulary","Fluency"], strengths:["Phonics & Decoding"],
    activities:["Afterschool Target Group","7% Target Group"] },
  { id:"S02", name:"Jordan K.",  grade:4, iready:478, growth:3,  sim:64, att:94.9, nys_prior:2, ell:false, iep:false,
    skill_gaps:["Reading Comprehension","Fluency"], strengths:["Phonological Awareness","Vocabulary"],
    activities:["Theater Club","Student Council"] },
  { id:"S03", name:"Sam L.",     grade:7, iready:441, growth:-4, sim:38, att:88.9, nys_prior:1, ell:true,  iep:false,
    skill_gaps:["Reading Comprehension","Vocabulary","Grammar & Conventions"], strengths:[],
    activities:["ASPIRA","Afterschool Target Group","7% Target Group"] },
  { id:"S04", name:"Riley P.",   grade:5, iready:510, growth:12, sim:74, att:97.2, nys_prior:2, ell:false, iep:false,
    skill_gaps:["Writing","Grammar & Conventions"], strengths:["Fluency","Vocabulary","Reading Comprehension"],
    activities:["Debate Team","Town Hall Leaders"] },
  { id:"S05", name:"Casey W.",   grade:8, iready:541, growth:18, sim:81, att:98.3, nys_prior:3, ell:false, iep:false,
    skill_gaps:[], strengths:["Reading Comprehension","Writing","Vocabulary"],
    activities:["Student Council","Debate Team","Math Olympiad"] },
  { id:"S06", name:"Morgan D.",  grade:3, iready:461, growth:6,  sim:68, att:91.7, nys_prior:2, ell:false, iep:false,
    skill_gaps:["Fluency","Phonics & Decoding"], strengths:["Phonological Awareness"],
    activities:["Choir","Bubble Group"] },
  { id:"S07", name:"Taylor B.",  grade:6, iready:508, growth:15, sim:71, att:96.1, nys_prior:3, ell:false, iep:false,
    skill_gaps:["Writing"], strengths:["Fluency","Reading Comprehension"],
    activities:["Theater Club","Town Hall Leaders","ASPIRA"] },
  { id:"S08", name:"Jamie F.",   grade:7, iready:474, growth:9,  sim:58, att:90.6, nys_prior:2, ell:false, iep:true,
    skill_gaps:["Reading Comprehension","Vocabulary","Algebra & Patterns"], strengths:["Phonics & Decoding"],
    activities:["Afterschool Target Group","Robotics Club"] },
  { id:"S09", name:"Drew Q.",    grade:4, iready:558, growth:22, sim:92, att:98.9, nys_prior:4, ell:false, iep:false,
    skill_gaps:[], strengths:["Reading Comprehension","Vocabulary","Writing"],
    activities:["Math Olympiad","Student Council","Robotics Club"] },
  { id:"S10", name:"Avery N.",   grade:8, iready:468, growth:4,  sim:62, att:93.9, nys_prior:2, ell:false, iep:false,
    skill_gaps:["Fluency","Reading Comprehension"], strengths:["Phonological Awareness","Phonics & Decoding"],
    activities:["ASPIRA","Choir"] },
  { id:"S11", name:"Quinn R.",   grade:5, iready:452, growth:-1, sim:47, att:89.4, nys_prior:1, ell:true,  iep:false,
    skill_gaps:["Vocabulary","Reading Comprehension","Fluency"], strengths:[],
    activities:["Afterschool Target Group","7% Target Group","ASPIRA"] },
  { id:"S12", name:"Blake S.",   grade:6, iready:495, growth:8,  sim:69, att:95.5, nys_prior:2, ell:false, iep:false,
    skill_gaps:["Writing","Grammar & Conventions"], strengths:["Reading Comprehension","Fluency"],
    activities:["Art Club","Bubble Group","Theater Club"] },
];

// Core grouping algorithm
function generateGroups(students, subject) {
  const groups = [];
  const isMath = subject === "Math";

  // Use subject-appropriate score and skill fields
  const getScore   = s => isMath ? (s.math_score  ?? s.iready) : s.iready;
  const getGrowth  = s => isMath ? (s.math_growth ?? s.growth) : s.growth;
  const getGaps    = s => isMath ? (s.math_gaps   ?? s.skill_gaps ?? []) : (s.ela_gaps ?? s.skill_gaps ?? []);
  const getNYS     = s => isMath ? (s.nys_math    ?? s.nys_prior) : (s.nys_ela ?? s.nys_prior);

  // GROUP 1 — Intensive: 2+ Below Grade, multiple gaps, declining
  const intensive = students.filter(s =>
    getScore(s) < 460 && getGaps(s).length >= 2 && (getGrowth(s) <= 0 || getNYS(s) === 1)
  );

  // GROUP 2 — Strategic: 1 Below Grade or stagnant growth
  const strategic = students.filter(s =>
    !intensive.find(i => i.id === s.id) &&
    (getScore(s) >= 460 && getScore(s) < 490 || (getScore(s) < 510 && getGrowth(s) < 5)) &&
    getGaps(s).length >= 1
  );

  // GROUP 3 — Benchmark: On grade, minor gaps
  const benchmark = students.filter(s =>
    !intensive.find(i => i.id === s.id) &&
    !strategic.find(i => i.id === s.id) &&
    getScore(s) >= 490 && getScore(s) < 530 && getGaps(s).length > 0
  );

  // GROUP 4 — Extension: At/Above grade, ready for enrichment
  const extension = students.filter(s =>
    !intensive.find(i => i.id === s.id) &&
    !strategic.find(i => i.id === s.id) &&
    !benchmark.find(i => i.id === s.id) &&
    getScore(s) >= 530
  );

  // Find shared focus skills per group using subject-appropriate gaps
  const topSkills = (grp) => {
    const freq = {};
    grp.forEach(s => getGaps(s).forEach(sk => { freq[sk] = (freq[sk] || 0) + 1; }));
    return Object.entries(freq).sort((a,b)=>b[1]-a[1]).slice(0,3).map(e=>e[0]);
  };

  const meetingFreq = (tier) => ({
    "Intensive": { times: 5, duration: 20, unit: "per week", rationale: "Daily small group — critical for students 2+ years below with declining growth" },
    "Strategic":  { times: 3, duration: 20, unit: "per week", rationale: "3x weekly — addresses significant gaps before they widen further" },
    "Benchmark":  { times: 2, duration: 15, unit: "per week", rationale: "2x weekly — targeted skill work to close minor gaps and accelerate" },
    "Extension":  { times: 1, duration: 20, unit: "per week", rationale: "Weekly enrichment — deepen complexity and build toward advanced NYS performance" },
  })[tier];

  const trajectoryGain = (tier) => ({
    "Intensive": { weeks8: 4, weeks16: 9,  weeks24: 14, nys_level_gain: "+0.5–1 Level" },
    "Strategic":  { weeks8: 6, weeks16: 12, weeks24: 18, nys_level_gain: "+0.5 Level" },
    "Benchmark":  { weeks8: 8, weeks16: 15, weeks24: 20, nys_level_gain: "Maintain L3 → L4 possible" },
    "Extension":  { weeks8:12, weeks16: 20, weeks24: 28, nys_level_gain: "Secure L4" },
  })[tier];

  const colors = {
    "Intensive": T.rose,
    "Strategic":  T.amber,
    "Benchmark":  T.blue,
    "Extension":  T.teal,
  };

  const icons = { "Intensive":"🔴","Strategic":"🟡","Benchmark":"🔵","Extension":"🟢" };

  [
    { tier:"Intensive", students: intensive },
    { tier:"Strategic",  students: strategic },
    { tier:"Benchmark",  students: benchmark },
    { tier:"Extension",  students: extension },
  ].forEach(({ tier, students: grpStudents }) => {
    if (grpStudents.length === 0) return;
    const skills = topSkills(grpStudents);
    const freq   = meetingFreq(tier);
    const traj   = trajectoryGain(tier);
    groups.push({
      id: `${subject==="Math"?"MG":"GRP"}-${tier.toUpperCase().slice(0,3)}`,
      tier,
      color: colors[tier],
      icon:  icons[tier],
      students: grpStudents,
      focusSkills: skills.length ? skills : (subject==="Math" ? ["Advanced problem solving","Algebraic reasoning","Data analysis"] : ["Grade-level enrichment","Critical thinking","Complex text analysis"]),
      meetingFrequency: freq,
      trajectory: traj,
      rationale: buildRationale(tier, grpStudents, skills, subject),
    });
  });

  return groups;
}

function buildRationale(tier, students, skills, subject="ELA") {
  const isMath = subject === "Math";
  const avgScore  = Math.round(students.reduce((a,s)=>a+(isMath?(s.math_score??s.iready):s.iready),0)/students.length);
  const avgGrowth = (students.reduce((a,s)=>a+(isMath?(s.math_growth??s.growth):s.growth),0)/students.length).toFixed(1);
  const declining = students.filter(s=>isMath?(s.math_growth??s.growth)<0:s.growth<0).length;
  const iepCount  = students.filter(s=>s.iep).length;
  const ellCount  = students.filter(s=>s.ell).length;

  const lines = [];
  lines.push(`Avg iReady ${subject}: ${avgScore} · Avg growth: ${avgGrowth >= 0 ? '+' : ''}${avgGrowth} scale score points`);
  if (declining > 0) lines.push(`⚠ ${declining} student${declining>1?'s':''} showing negative ${subject} growth — intervention urgency is HIGH`);
  if (iepCount > 0)  lines.push(`${iepCount} student${iepCount>1?'s':''} with IEP — ensure alignment with IEP goals during sessions`);
  if (ellCount > 0)  lines.push(`${ellCount} ELL student${ellCount>1?'s':''} — incorporate ${isMath?"math vocabulary and visual models":"vocabulary scaffolds and sentence frames"}`);
  if (skills.length) lines.push(`Shared ${subject} skill gaps: ${skills.join(', ')}`);
  if (isMath) lines.push("Use manipulatives, number lines, and visual models to build conceptual understanding");
  return lines;
}

// ─────────────────────────────────────────────────────────────
// PROGRESS MONITORING DATA
// ─────────────────────────────────────────────────────────────

const INITIAL_SESSIONS = {
  "GRP-INT": [
    { id:"ses1", date:"2025-09-09", skill:"Reading Comprehension", studentsPresent:["S01","S03","S11"], notes:"Introduced main idea strategy. Students struggled with inferencing. Used graphic organizer.", exitTicketScores:{ S01:40, S03:35, S11:38 }, teacherRating:2 },
    { id:"ses2", date:"2025-09-12", skill:"Reading Comprehension", studentsPresent:["S01","S03","S11"], notes:"Repeated main idea with shorter texts. Alex showed improvement. Sam still needs scaffolding.", exitTicketScores:{ S01:48, S03:38, S11:42 }, teacherRating:3 },
    { id:"ses3", date:"2025-09-15", skill:"Vocabulary",            studentsPresent:["S01","S11"],       notes:"Context clue strategies. Quinn absent. Good engagement from Alex.", exitTicketScores:{ S01:52, S11:44 }, teacherRating:3 },
    { id:"ses4", date:"2025-09-18", skill:"Vocabulary",            studentsPresent:["S01","S03","S11"], notes:"Applied vocabulary strategies to grade-level text. All students showing growth.", exitTicketScores:{ S01:58, S03:45, S11:50 }, teacherRating:4 },
    { id:"ses5", date:"2025-09-22", skill:"Fluency",               studentsPresent:["S01","S03","S11"], notes:"Partner reading fluency practice. ORF scores improving for all three.", exitTicketScores:{ S01:62, S03:50, S11:54 }, teacherRating:4 },
    { id:"ses6", date:"2025-09-25", skill:"Reading Comprehension", studentsPresent:["S01","S03","S11"], notes:"Returned to comprehension with longer passage. Stronger performance across board.", exitTicketScores:{ S01:65, S03:54, S11:58 }, teacherRating:4 },
  ],
  "GRP-STR": [
    { id:"ses1", date:"2025-09-10", skill:"Fluency",               studentsPresent:["S02","S08","S10"], notes:"Timed reading passages. Jordan and Avery near benchmark. Jamie below.", exitTicketScores:{ S02:62, S08:50, S10:58 }, teacherRating:3 },
    { id:"ses2", date:"2025-09-15", skill:"Reading Comprehension", studentsPresent:["S02","S08","S10"], notes:"Text structure lesson. Good engagement. Avery making strong connections.", exitTicketScores:{ S02:66, S08:55, S10:64 }, teacherRating:3 },
    { id:"ses3", date:"2025-09-18", skill:"Fluency",               studentsPresent:["S02","S10"],       notes:"Repeated readings protocol. Both students above target fluency rate.", exitTicketScores:{ S02:70, S10:68 }, teacherRating:4 },
    { id:"ses4", date:"2025-09-22", skill:"Vocabulary",            studentsPresent:["S02","S08","S10"], notes:"Word morphology lesson. Jamie struggling with academic vocabulary.", exitTicketScores:{ S02:72, S08:58, S10:70 }, teacherRating:3 },
  ],
  "GRP-BEN": [
    { id:"ses1", date:"2025-09-11", skill:"Writing",               studentsPresent:["S04","S06","S12"], notes:"Paragraph structure and evidence citing. All students engaged.", exitTicketScores:{ S04:70, S06:65, S12:68 }, teacherRating:3 },
    { id:"ses2", date:"2025-09-16", skill:"Grammar & Conventions", studentsPresent:["S04","S06","S12"], notes:"Comma usage and complex sentences. Riley picked this up quickly.", exitTicketScores:{ S04:76, S06:68, S12:72 }, teacherRating:4 },
    { id:"ses3", date:"2025-09-20", skill:"Writing",               studentsPresent:["S04","S12"],       notes:"Extended response practice with rubric. Strong work from both.", exitTicketScores:{ S04:80, S12:75 }, teacherRating:4 },
  ],
  "GRP-EXT": [],
  // Math intervention group sessions
  "MG-INT": [
    { id:"m1", date:"2025-09-10", skill:"Number Sense",  studentsPresent:["S01","S08","S11"], notes:"Introduced number line strategies for integers. Alex engaged, Quinn needed extra scaffolding.", exitTicketScores:{ S01:38, S08:35, S11:32 }, teacherRating:2 },
    { id:"m2", date:"2025-09-13", skill:"Number Sense",  studentsPresent:["S01","S08","S11"], notes:"Repeated number sense with manipulatives. Improvement across all three students.", exitTicketScores:{ S01:45, S08:42, S11:39 }, teacherRating:3 },
    { id:"m3", date:"2025-09-17", skill:"Fractions",     studentsPresent:["S01","S08","S11"], notes:"Fraction models with visual aids. Alex struggled with equivalent fractions.", exitTicketScores:{ S01:48, S08:44, S11:41 }, teacherRating:3 },
    { id:"m4", date:"2025-09-20", skill:"Fractions",     studentsPresent:["S01","S11"],       notes:"Fraction operations. Jamie absent. Quinn showing growth with bar models.", exitTicketScores:{ S01:54, S11:47 }, teacherRating:3 },
    { id:"m5", date:"2025-09-24", skill:"Algebra",       studentsPresent:["S01","S08","S11"], notes:"Simple equation solving. All students improving. Alex now at near-benchmark.", exitTicketScores:{ S01:59, S08:52, S11:53 }, teacherRating:4 },
  ],
  "MG-STR": [
    { id:"m1", date:"2025-09-11", skill:"Fractions",     studentsPresent:["S03","S04","S07","S12"], notes:"Fraction word problems. Taylor and Blake performing near benchmark.", exitTicketScores:{ S03:55, S04:58, S07:62, S12:56 }, teacherRating:3 },
    { id:"m2", date:"2025-09-16", skill:"Algebra",       studentsPresent:["S03","S04","S07","S12"], notes:"One-step equations. Riley strong. Sam needs more support with variables.", exitTicketScores:{ S03:58, S04:64, S07:66, S12:60 }, teacherRating:3 },
    { id:"m3", date:"2025-09-20", skill:"Geometry",      studentsPresent:["S04","S07","S12"],       notes:"Area and perimeter. Good engagement with manipulatives.", exitTicketScores:{ S04:68, S07:70, S12:65 }, teacherRating:4 },
  ],
  "MG-BEN": [
    { id:"m1", date:"2025-09-12", skill:"Algebra",       studentsPresent:["S02","S06","S10"], notes:"Multi-step equations. Jordan and Avery performing well.", exitTicketScores:{ S02:68, S06:65, S10:70 }, teacherRating:3 },
    { id:"m2", date:"2025-09-18", skill:"Operations",    studentsPresent:["S02","S06","S10"], notes:"Order of operations. All students above proficiency.", exitTicketScores:{ S02:73, S06:69, S10:74 }, teacherRating:4 },
  ],
  "MG-EXT": [],
};

// Build per-student progress over sessions
function buildStudentProgress(sessions, studentId) {
  return sessions
    .filter(s => s.studentsPresent.includes(studentId) && s.exitTicketScores[studentId])
    .map(s => ({ date: s.date, score: s.exitTicketScores[studentId], skill: s.skill }));
}

// Project trajectory from current trend
function projectTrajectory(sessions, studentId, weeksAhead = 12) {
  const progress = buildStudentProgress(sessions, studentId);
  if (progress.length < 2) return [];
  const scores = progress.map(p => p.score);
  const avgGain = (scores[scores.length-1] - scores[0]) / (scores.length - 1);
  const last = scores[scores.length-1];
  const lastDate = new Date(progress[progress.length-1].date);
  const projected = [];
  for (let w = 1; w <= weeksAhead; w++) {
    const d = new Date(lastDate);
    d.setDate(d.getDate() + w * 7);
    projected.push({
      date: d.toISOString().slice(0,10),
      projected: Math.min(100, Math.round(last + avgGain * w * 0.8)),
    });
  }
  return projected;
}

// ─────────────────────────────────────────────────────────────
// UTILITY COMPONENTS
// ─────────────────────────────────────────────────────────────

const Badge = ({ text, color, bg }) => (
  <span style={{
    background: bg || color + "18",
    color, border: `1px solid ${color}44`,
    borderRadius: 99, padding: "3px 10px",
    fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
  }}>{text}</span>
);

const Chip = ({ text, color = T.blue }) => (
  <span style={{
    background: color + "15", color,
    borderRadius: 6, padding: "3px 9px",
    fontSize: 11, fontWeight: 600,
    border: `1px solid ${color}30`,
    display: "inline-block", margin: "2px 3px",
  }}>{text}</span>
);

const Card = ({ children, style = {}, onClick }) => (
  <div onClick={onClick} style={{
    background: T.white, borderRadius: 14,
    border: `1px solid ${T.mist}`,
    boxShadow: "0 2px 12px rgba(13,27,42,.06)",
    padding: "20px 22px",
    cursor: onClick ? "pointer" : "default",
    transition: "box-shadow .18s",
    ...style,
  }}>{children}</div>
);

const SectionTitle = ({ icon, title, sub }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <span style={{ fontSize: 22 }}>{icon}</span>
      <span style={{ fontSize: 20, fontWeight: 900, color: T.navy }}>{title}</span>
    </div>
    {sub && <div style={{ fontSize: 12, color: T.slate, marginTop: 4, marginLeft: 32 }}>{sub}</div>}
  </div>
);

const KPI = ({ label, value, sub, color = T.blue, icon }) => (
  <div style={{
    background: T.white, borderRadius: 12,
    padding: "16px 18px", border: `1px solid ${T.mist}`,
    borderTop: `3px solid ${color}`,
    boxShadow: "0 1px 8px rgba(13,27,42,.05)",
  }}>
    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
      <span style={{ fontSize:10, color:T.slate, fontWeight:700, letterSpacing:".07em", textTransform:"uppercase" }}>{label}</span>
      <span style={{ fontSize:16 }}>{icon}</span>
    </div>
    <div style={{ fontSize:28, fontWeight:900, color:T.navy }}>{value}</div>
    {sub && <div style={{ fontSize:11, color, fontWeight:600, marginTop:3 }}>{sub}</div>}
  </div>
);

const Avatar = ({ name, color }) => (
  <div style={{
    width:28, height:28, borderRadius:"50%",
    background: color + "25", color,
    display:"flex", alignItems:"center", justifyContent:"center",
    fontSize:11, fontWeight:800, border:`1.5px solid ${color}55`,
    flexShrink:0,
  }}>
    {name.split(" ").map(n=>n[0]).join("")}
  </div>
);

// ─────────────────────────────────────────────────────────────
// SECTION: SMART GROUPS
// ─────────────────────────────────────────────────────────────

function SmartGroupsSection({ groups, onSelectGroup, selectedGroupId, subject="ELA" }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <SectionTitle icon="🧩" title={`${subject} Smart Group Recommendations`}
        sub={`Auto-generated from ${subject} iReady diagnostics · simulation trajectories · prior NYS scores · attendance · ${subject} skill gap analysis`} />

      {/* Legend */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        {[
          { tier:"Intensive",  color:T.rose,  desc: subject==="Math" ? "2+ below grade · multiple math gaps · daily sessions" : "2+ years below · critical gaps · daily sessions" },
          { tier:"Strategic",  color:T.amber, desc: subject==="Math" ? "1 below grade · targeted math skills · 3x per week" : "1 year below · targeted gaps · 3x per week" },
          { tier:"Benchmark",  color:T.blue,  desc: subject==="Math" ? "On grade · minor math gaps · 2x per week" : "On grade · minor gaps · 2x per week" },
          { tier:"Extension",  color:T.teal,  desc: subject==="Math" ? "At/Above grade · math enrichment · weekly" : "At/Above grade · enrichment · weekly" },
        ].map(g => (
          <div key={g.tier} style={{
            background: g.color + "10", border:`1.5px solid ${g.color}44`,
            borderRadius:10, padding:"10px 14px", borderLeft:`4px solid ${g.color}`,
          }}>
            <div style={{ fontWeight:800, fontSize:13, color:g.color }}>{g.tier}</div>
            <div style={{ fontSize:11, color:T.slate, marginTop:3, lineHeight:1.5 }}>{g.desc}</div>
          </div>
        ))}
      </div>

      {/* Group Cards */}
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
        {groups.map(group => (
          <Card key={group.id}
            onClick={() => onSelectGroup(group.id === selectedGroupId ? null : group.id)}
            style={{
              border: selectedGroupId === group.id
                ? `2px solid ${group.color}`
                : `1px solid ${T.mist}`,
              boxShadow: selectedGroupId === group.id
                ? `0 4px 20px ${group.color}25`
                : "0 2px 12px rgba(13,27,42,.06)",
            }}>
            {/* Group Header */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{
                  width:44, height:44, borderRadius:12,
                  background: group.color + "18",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:22, border:`2px solid ${group.color}33`,
                }}>
                  {group.icon}
                </div>
                <div>
                  <div style={{ fontWeight:900, fontSize:16, color:T.navy }}>
                    {group.tier} Group
                    <span style={{ fontSize:12, color:T.slate, fontWeight:500, marginLeft:8 }}>({group.id})</span>
                  </div>
                  <div style={{ fontSize:12, color:T.slate, marginTop:2 }}>
                    {group.students.length} students · {group.meetingFrequency.times}× {group.meetingFrequency.unit} · {group.meetingFrequency.duration} min/session
                  </div>
                </div>
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                <Badge text={`${group.meetingFrequency.times}× ${group.meetingFrequency.unit}`} color={group.color} />
                <Badge text={`${group.trajectory.nys_level_gain}`} color={T.teal} />
              </div>
            </div>

            {/* Students */}
            <div style={{ display:"flex", gap:8, marginTop:16, flexWrap:"wrap" }}>
              {group.students.map(s => (
                <div key={s.id} style={{
                  display:"flex", alignItems:"center", gap:6,
                  background: T.bg, borderRadius:8, padding:"5px 10px",
                  border:`1px solid ${T.mist}`,
                }}>
                  <Avatar name={s.name} color={group.color} />
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:T.navy }}>{s.name}</div>
                    <div style={{ fontSize:10, color:T.slate }}>Gr{s.grade} · iR:{s.iready} · {s.growth>=0?"+":""}{s.growth}</div>
                  </div>
                  {s.iep && <Badge text="IEP" color={T.purple} />}
                  {s.ell && <Badge text="ELL" color={T.sky} />}
                </div>
              ))}
            </div>

            {/* Focus Skills */}
            <div style={{ marginTop:16, display:"flex", gap:16, flexWrap:"wrap" }}>
              <div style={{ flex:1, minWidth:200 }}>
                <div style={{ fontSize:11, fontWeight:700, color:T.slate, marginBottom:6, textTransform:"uppercase", letterSpacing:".05em" }}>
                  Focus Skills (Shared Gaps)
                </div>
                <div style={{ display:"flex", flexWrap:"wrap" }}>
                  {group.focusSkills.map((sk,i) => (
                    <Chip key={i} text={`${i+1}. ${sk}`} color={group.color} />
                  ))}
                </div>
              </div>
              <div style={{ flex:1, minWidth:200 }}>
                <div style={{ fontSize:11, fontWeight:700, color:T.slate, marginBottom:6, textTransform:"uppercase", letterSpacing:".05em" }}>
                  Session Rationale
                </div>
                <div style={{ fontSize:12, color:T.slate, lineHeight:1.6 }}>
                  {group.meetingFrequency.rationale}
                </div>
              </div>
            </div>

            {/* Rationale bullets */}
            <div style={{
              marginTop:14, background: group.color + "08",
              borderRadius:10, padding:"10px 14px",
              border:`1px solid ${group.color}22`,
            }}>
              <div style={{ fontSize:11, fontWeight:700, color:group.color, marginBottom:6, textTransform:"uppercase", letterSpacing:".04em" }}>
                Data Analysis
              </div>
              {group.rationale.map((r,i) => (
                <div key={i} style={{ fontSize:12, color:T.ink, lineHeight:1.6, display:"flex", gap:6 }}>
                  <span style={{ color:group.color, marginTop:2 }}>▸</span>
                  <span>{r}</span>
                </div>
              ))}
            </div>

            {/* Trajectory Preview */}
            <div style={{ marginTop:14, display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
              {[
                { label:"8 Weeks",  val:`+${group.trajectory.weeks8} pts`,  color:T.sky },
                { label:"16 Weeks", val:`+${group.trajectory.weeks16} pts`, color:T.blue },
                { label:"24 Weeks", val:`+${group.trajectory.weeks24} pts`, color:T.teal },
              ].map(t => (
                <div key={t.label} style={{
                  background:T.bg, borderRadius:8, padding:"8px 12px", textAlign:"center",
                  border:`1px solid ${T.mist}`,
                }}>
                  <div style={{ fontSize:10, color:T.slate, fontWeight:600 }}>{t.label} Projected Gain</div>
                  <div style={{ fontSize:18, fontWeight:900, color:t.color, marginTop:2 }}>{t.val}</div>
                </div>
              ))}
            </div>

            {selectedGroupId === group.id && (
              <div style={{
                marginTop:14, background:T.navy, borderRadius:10, padding:"10px 16px",
                display:"flex", alignItems:"center", gap:8,
              }}>
                <span style={{ fontSize:16 }}>👆</span>
                <span style={{ fontSize:12, color:"#A3C4E8" }}>
                  Click "Progress Monitor" tab to log sessions and track this group's impact over time
                </span>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SECTION: TRAJECTORY PROJECTIONS
// ─────────────────────────────────────────────────────────────

function TrajectorySection({ groups, subject="ELA" }) {
  const [focusGroup, setFocusGroup] = useState(groups[0]?.id);

  const group = groups.find(g => g.id === focusGroup);
  if (!group) return null;

  // Build multi-week projection chart per tier
  const projData = [];
  for (let w = 0; w <= 24; w++) {
    const row = { week: `W${w}` };
    groups.forEach(g => {
      const base = { "Intensive":38, "Strategic":58, "Benchmark":70, "Extension":85 }[g.tier];
      const rate = { "Intensive":0.55, "Strategic":0.50, "Benchmark":0.42, "Extension":0.55 }[g.tier];
      row[g.tier] = Math.min(99, Math.round(base + w * rate * (g.meetingFrequency.times / 5 * 1.2)));
    });
    projData.push(row);
  }

  // NYS exam date reference (approx week 32 from Sep start)
  const nysWeek = 32;

  const nysThresholds = [
    { y:50, label:"L1→L2 threshold", color:T.amber },
    { y:65, label:"L2→L3 (Proficiency)", color:T.blue },
    { y:80, label:"L3→L4 threshold", color:T.teal },
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <SectionTitle icon="📈" title={`${subject} Impact Trajectory Projections`}
        sub="Projected score trajectory per group if meeting frequency and focus skill instruction are maintained consistently" />

      {/* Group selector */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
        {groups.map(g => (
          <button key={g.id} onClick={() => setFocusGroup(g.id)} style={{
            border: `2px solid ${focusGroup===g.id ? g.color : T.mist}`,
            background: focusGroup===g.id ? g.color+"15" : T.white,
            borderRadius:8, padding:"6px 14px", cursor:"pointer",
            fontSize:12, fontWeight:700, color:focusGroup===g.id ? g.color : T.slate,
            transition:"all .15s",
          }}>
            {g.icon} {g.tier} ({g.students.length})
          </button>
        ))}
      </div>

      {/* All-group trajectory */}
      <Card>
        <div style={{ fontSize:14, fontWeight:800, color:T.navy, marginBottom:4 }}>All Groups — 24-Week Projection</div>
        <div style={{ fontSize:11, color:T.slate, marginBottom:16 }}>Assumes consistent instruction at recommended frequency · Dashed lines = NYS proficiency thresholds</div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={projData}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.mist}/>
            <XAxis dataKey="week" tick={{fontSize:10, fill:T.slate}}
              tickFormatter={v => v.replace("W","Wk ")}/>
            <YAxis domain={[30,100]} tick={{fontSize:10, fill:T.slate}}/>
            <Tooltip contentStyle={{borderRadius:10, fontSize:12, border:`1px solid ${T.mist}`}}
              formatter={(v,n) => [`${v}%`, n]}/>
            <Legend wrapperStyle={{fontSize:12}}/>
            {nysThresholds.map(nt => (
              <ReferenceLine key={nt.label} y={nt.y} stroke={nt.color}
                strokeDasharray="5 3"
                label={{value:nt.label, fill:nt.color, fontSize:10, position:"right"}}/>
            ))}
            {groups.map(g => (
              <Line key={g.tier} type="monotone" dataKey={g.tier}
                stroke={g.color} strokeWidth={2.5} dot={false}
                name={`${g.icon} ${g.tier}`}/>
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Selected group deep-dive */}
      {group && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <Card>
            <div style={{ fontSize:14, fontWeight:800, color:T.navy, marginBottom:4 }}>
              {group.icon} {group.tier} — Projected Impact by Consistency
            </div>
            <div style={{ fontSize:11, color:T.slate, marginBottom:16 }}>
              What happens if sessions are consistent vs. inconsistent
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={[...Array(25)].map((_,w)=>({
                week:`W${w}`,
                consistent: Math.min(99, Math.round(
                  ({Intensive:38,Strategic:58,Benchmark:70,Extension:85}[group.tier]) +
                  w * ({Intensive:0.58,Strategic:0.52,Benchmark:0.44,Extension:0.58}[group.tier])
                )),
                inconsistent: Math.min(99, Math.round(
                  ({Intensive:38,Strategic:58,Benchmark:70,Extension:85}[group.tier]) +
                  w * ({Intensive:0.28,Strategic:0.24,Benchmark:0.20,Extension:0.25}[group.tier])
                )),
              }))}>
                <defs>
                  <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={group.color} stopOpacity={0.2}/>
                    <stop offset="95%" stopColor={group.color} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.mist}/>
                <XAxis dataKey="week" tick={{fontSize:9, fill:T.slate}} interval={3}/>
                <YAxis domain={[30,100]} tick={{fontSize:10, fill:T.slate}}/>
                <Tooltip contentStyle={{borderRadius:10, fontSize:12}}/>
                <Legend wrapperStyle={{fontSize:11}}/>
                <Area type="monotone" dataKey="consistent" stroke={group.color}
                  fill="url(#gc)" strokeWidth={2} name="Consistent (recommended freq)"/>
                <Line type="monotone" dataKey="inconsistent" stroke={T.slate}
                  strokeWidth={1.5} strokeDasharray="5 3" dot={false} name="Inconsistent"/>
                <ReferenceLine y={65} stroke={T.blue} strokeDasharray="4 2"
                  label={{value:"Proficiency", fill:T.blue, fontSize:10}}/>
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          <Card>
            <div style={{ fontSize:14, fontWeight:800, color:T.navy, marginBottom:16 }}>
              Projected NYS Outcome by Group
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {groups.map(g => {
                const current = {Intensive:1.2, Strategic:2.1, Benchmark:2.8, Extension:3.7}[g.tier];
                const projected = {Intensive:1.8, Strategic:2.6, Benchmark:3.2, Extension:4.0}[g.tier];
                return (
                  <div key={g.id} style={{
                    background:g.color+"08", borderRadius:10, padding:"12px 14px",
                    border:`1px solid ${g.color}22`,
                  }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                      <span style={{ fontWeight:700, fontSize:13, color:T.navy }}>{g.icon} {g.tier}</span>
                      <div style={{ display:"flex", gap:8 }}>
                        <Badge text={`Now: L${Math.round(current)}`} color={T.slate}/>
                        <Badge text={`→ L${Math.round(projected)} projected`} color={g.color}/>
                      </div>
                    </div>
                    <div style={{ background:T.mist, borderRadius:99, height:8 }}>
                      <div style={{
                        height:8, borderRadius:99, background:g.color,
                        width:`${Math.min(100,(projected/4)*100)}%`, transition:"width .5s",
                      }}/>
                    </div>
                    <div style={{ fontSize:11, color:T.slate, marginTop:5 }}>
                      {g.trajectory.nys_level_gain} if {g.meetingFrequency.times}× weekly maintained for 24 weeks
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SECTION: PROGRESS MONITORING
// ─────────────────────────────────────────────────────────────

function ProgressMonitorSection({ groups, sessions, setSessions, subject="ELA" }) {
  const [activeGroup, setActiveGroup] = useState(groups[0]?.id);
  const [showLogForm, setShowLogForm] = useState(false);
  const [focusStudent, setFocusStudent] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0,10),
    skill: "",
    subSkill: "",
    studentsPresent: [],
    notes: "",
    scores: {},
    teacherRating: 3,
  });

  const group = groups.find(g => g.id === activeGroup);
  if (!group) return null;
  const groupSessions = sessions[activeGroup] || [];

  // Build chart data: average score per session
  const avgProgressData = groupSessions.map((ses, idx) => {
    const scores = Object.values(ses.exitTicketScores);
    const avg = scores.length ? Math.round(scores.reduce((a,b)=>a+b,0)/scores.length) : null;
    return { session: `Ses ${idx+1}`, avg, date: ses.date, skill: ses.skill };
  });

  // Per-student lines
  const allStudentIds = [...new Set(groupSessions.flatMap(s => s.studentsPresent))];

  const studentProgressLines = allStudentIds.map(sid => {
    const student = STUDENTS.find(s=>s.id===sid);
    const points = groupSessions
      .filter(ses => ses.exitTicketScores[sid])
      .map((ses, idx) => ({ session: `Ses ${groupSessions.indexOf(ses)+1}`, score: ses.exitTicketScores[sid] }));
    return { student, points };
  });

  // Chart data merged
  const multiLineData = groupSessions.map((ses, idx) => {
    const row = { session: `S${idx+1}`, date: ses.date };
    allStudentIds.forEach(sid => {
      if (ses.exitTicketScores[sid]) row[sid] = ses.exitTicketScores[sid];
    });
    return row;
  });

  const studentColors = [T.blue, T.teal, T.rose, T.amber, T.purple, T.sky];

  const handleAddSession = () => {
    if (!form.skill || form.studentsPresent.length === 0) return;
    const newSes = {
      id: `ses${groupSessions.length+1}`,
      date: form.date,
      skill: form.skill,
      subSkill: form.subSkill,
      studentsPresent: form.studentsPresent,
      notes: form.notes,
      exitTicketScores: form.scores,
      teacherRating: form.teacherRating,
    };
    setSessions(prev => ({
      ...prev,
      [activeGroup]: [...(prev[activeGroup]||[]), newSes],
    }));
    setShowLogForm(false);
    setForm({ date: new Date().toISOString().slice(0,10), skill:"", subSkill:"", studentsPresent:[], notes:"", scores:{}, teacherRating:3 });
  };

  // Impact summary
  const firstAvg = avgProgressData.length > 0 ? avgProgressData[0].avg : null;
  const lastAvg  = avgProgressData.length > 0 ? avgProgressData[avgProgressData.length-1].avg : null;
  const gainSoFar = firstAvg && lastAvg ? lastAvg - firstAvg : 0;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <SectionTitle icon="📋" title={`${subject} Progress Monitoring`}
        sub="Log small group sessions · Track exit ticket scores · Monitor individual and group trajectory"/>

      {/* Group selector */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
        {groups.map(g => (
          <button key={g.id} onClick={()=>{setActiveGroup(g.id);setFocusStudent(null);setShowLogForm(false);}} style={{
            border:`2px solid ${activeGroup===g.id ? g.color : T.mist}`,
            background: activeGroup===g.id ? g.color : T.white,
            borderRadius:8, padding:"7px 16px", cursor:"pointer",
            fontSize:12, fontWeight:700,
            color: activeGroup===g.id ? T.white : T.slate,
            transition:"all .15s",
          }}>
            {g.icon} {g.tier} · {(sessions[g.id]||[]).length} sessions
          </button>
        ))}
      </div>

      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        <KPI label="Sessions Logged"  value={groupSessions.length}   sub="this group so far"    color={group.color} icon="📅"/>
        <KPI label="Avg Score Gain"   value={gainSoFar > 0 ? `+${gainSoFar}%` : "—"} sub="first → last session" color={T.teal} icon="📈"/>
        <KPI label="Students Tracked" value={allStudentIds.length}   sub="with exit ticket data" color={T.blue}       icon="👥"/>
        <KPI label="Sessions to Goal" value={Math.max(0, group.meetingFrequency.times*8 - groupSessions.length)} sub={`${group.meetingFrequency.times}×/wk for 8 wks`} color={T.amber} icon="🎯"/>
      </div>

      {/* Charts */}
      {groupSessions.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16 }}>
          <Card>
            <div style={{ fontSize:14, fontWeight:800, color:T.navy, marginBottom:4 }}>
              Individual Student Progress — Exit Ticket Scores
            </div>
            <div style={{ fontSize:11, color:T.slate, marginBottom:14 }}>
              Click a student name in the legend to isolate their trajectory
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={multiLineData}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.mist}/>
                <XAxis dataKey="session" tick={{fontSize:11, fill:T.slate}}/>
                <YAxis domain={[20,100]} tick={{fontSize:11, fill:T.slate}}/>
                <Tooltip contentStyle={{borderRadius:10, fontSize:12}}
                  labelFormatter={l => {
                    const ses = groupSessions[parseInt(l.replace("S",""))-1];
                    return ses ? `${l} · ${ses.date} · ${ses.skill}${ses.subSkill?' → '+ses.subSkill:''}` : l;
                  }}
                />
                <Legend wrapperStyle={{fontSize:11}}/>
                <ReferenceLine y={65} stroke={T.blue} strokeDasharray="4 2"
                  label={{value:"Proficiency target", fill:T.blue, fontSize:10}}/>
                {allStudentIds.map((sid, i) => {
                  const stu = STUDENTS.find(s=>s.id===sid);
                  return (
                    <Line key={sid} type="monotone" dataKey={sid}
                      stroke={studentColors[i % studentColors.length]}
                      strokeWidth={2} dot={{r:4}}
                      name={stu?.name || sid}
                      connectNulls={true}/>
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <Card>
            <div style={{ fontSize:14, fontWeight:800, color:T.navy, marginBottom:14 }}>
              Group Average + Teacher Rating
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={avgProgressData}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.mist}/>
                <XAxis dataKey="session" tick={{fontSize:10,fill:T.slate}}/>
                <YAxis yAxisId="left" domain={[20,100]} tick={{fontSize:10,fill:T.slate}}/>
                <YAxis yAxisId="right" orientation="right" domain={[0,5]} tick={{fontSize:10,fill:T.slate}}/>
                <Tooltip contentStyle={{borderRadius:10,fontSize:12}}/>
                <Legend wrapperStyle={{fontSize:11}}/>
                <ReferenceLine yAxisId="left" y={65} stroke={T.blue} strokeDasharray="3 2"/>
                <Bar yAxisId="left" dataKey="avg" fill={group.color} radius={[4,4,0,0]} name="Group Avg %"/>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {/* Session Log Table */}
      <Card>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:800, color:T.navy }}>Session Log</div>
            <div style={{ fontSize:11, color:T.slate }}>All logged sessions for {group.icon} {group.tier} group</div>
          </div>
          <button onClick={()=>setShowLogForm(!showLogForm)} style={{
            background: group.color, color:T.white, border:"none",
            borderRadius:8, padding:"8px 16px", cursor:"pointer",
            fontSize:12, fontWeight:700, display:"flex", alignItems:"center", gap:6,
          }}>
            {showLogForm ? "✕ Cancel" : "＋ Log New Session"}
          </button>
        </div>

        {/* Log Form */}
        {showLogForm && (
          <div style={{
            background: group.color+"08", border:`1.5px solid ${group.color}33`,
            borderRadius:12, padding:"18px 20px", marginBottom:18,
          }}>
            <div style={{ fontSize:13, fontWeight:800, color:group.color, marginBottom:14 }}>
              Log Session — {group.icon} {group.tier} Group
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:T.slate, display:"block", marginBottom:4 }}>SESSION DATE</label>
                <input type="date" value={form.date}
                  onChange={e=>setForm(f=>({...f, date:e.target.value}))}
                  style={{ width:"100%", padding:"7px 10px", borderRadius:7, border:`1px solid ${T.mist}`, fontSize:13, boxSizing:"border-box" }}/>
              </div>
              {/* Level 1 — Domain skill */}
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:T.slate, display:"block", marginBottom:4 }}>
                  FOCUS SKILL — DOMAIN
                </label>
                <select value={form.skill}
                  onChange={e=>setForm(f=>({...f, skill:e.target.value, subSkill:""}))}
                  style={{ width:"100%", padding:"7px 10px", borderRadius:7, border:`1px solid ${T.mist}`, fontSize:13, boxSizing:"border-box", fontFamily:"inherit" }}>
                  <option value="">Select domain...</option>
                  {group.focusSkills.length > 0 && (
                    <optgroup label="── Recommended for this group ──">
                      {group.focusSkills.map(sk=><option key={sk} value={sk}>{sk}</option>)}
                    </optgroup>
                  )}
                  <optgroup label="── All {subject} domains ──">
                    {(SKILL_DOMAINS[subject]||SKILL_DOMAINS.ELA).map(sk=><option key={sk} value={sk}>{sk}</option>)}
                  </optgroup>
                </select>
              </div>

              {/* Level 2 — Granular sub-skill (appears after Level 1 selected) */}
              {form.skill && SKILL_TAXONOMY[subject]?.[form.skill] && (
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:T.slate, display:"block", marginBottom:4 }}>
                    GRANULAR FOCUS SKILL
                    <span style={{ fontSize:10, color:T.teal, fontWeight:600, marginLeft:8 }}>
                      ↳ {form.skill}
                    </span>
                  </label>
                  <select value={form.subSkill}
                    onChange={e=>setForm(f=>({...f, subSkill:e.target.value}))}
                    style={{ width:"100%", padding:"7px 10px", borderRadius:7,
                      border:`2px solid ${T.teal}55`, fontSize:13,
                      boxSizing:"border-box", fontFamily:"inherit",
                      background:`${T.teal}08` }}>
                    <option value="">Select specific skill...</option>
                    {SKILL_TAXONOMY[subject][form.skill].map(sub=>(
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                  {form.subSkill && (
                    <div style={{ fontSize:11, color:T.teal, marginTop:5, fontWeight:600 }}>
                      ✓ Session focus: <strong>{form.skill}</strong> → <strong>{form.subSkill}</strong>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ marginTop:12 }}>
              <label style={{ fontSize:11, fontWeight:700, color:T.slate, display:"block", marginBottom:6 }}>STUDENTS PRESENT</label>
              <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                {group.students.map(s=>(
                  <label key={s.id} style={{
                    display:"flex", alignItems:"center", gap:6,
                    background: form.studentsPresent.includes(s.id) ? group.color+"18" : T.bg,
                    border:`1px solid ${form.studentsPresent.includes(s.id) ? group.color : T.mist}`,
                    borderRadius:7, padding:"5px 10px", cursor:"pointer",
                    fontSize:12, fontWeight:600, color:T.navy,
                  }}>
                    <input type="checkbox" checked={form.studentsPresent.includes(s.id)}
                      onChange={e=>{
                        setForm(f=>({...f,
                          studentsPresent: e.target.checked
                            ? [...f.studentsPresent, s.id]
                            : f.studentsPresent.filter(id=>id!==s.id)
                        }));
                      }} style={{accentColor:group.color}}/>
                    {s.name}
                  </label>
                ))}
              </div>
            </div>

            {/* Exit Ticket Scores */}
            {form.studentsPresent.length > 0 && (
              <div style={{ marginTop:12 }}>
                <label style={{ fontSize:11, fontWeight:700, color:T.slate, display:"block", marginBottom:6 }}>EXIT TICKET SCORES (0–100)</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
                  {form.studentsPresent.map(sid => {
                    const s = STUDENTS.find(st=>st.id===sid);
                    return (
                      <div key={sid} style={{ display:"flex", flexDirection:"column", gap:3, minWidth:80 }}>
                        <label style={{ fontSize:10, fontWeight:700, color:T.slate }}>{s?.name}</label>
                        <input type="number" min="0" max="100" placeholder="Score"
                          value={form.scores[sid]||""}
                          onChange={e=>setForm(f=>({...f, scores:{...f.scores,[sid]:parseInt(e.target.value)||0}}))}
                          style={{ width:"100%", padding:"5px 8px", borderRadius:6, border:`1px solid ${T.mist}`, fontSize:13, boxSizing:"border-box" }}/>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ marginTop:12 }}>
              <label style={{ fontSize:11, fontWeight:700, color:T.slate, display:"block", marginBottom:4 }}>SESSION NOTES</label>
              <textarea rows={3} value={form.notes}
                placeholder="What worked well? What needs to be revisited? Any student-specific observations?"
                onChange={e=>setForm(f=>({...f, notes:e.target.value}))}
                style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:`1px solid ${T.mist}`, fontSize:12, resize:"vertical", boxSizing:"border-box" }}/>
            </div>

            <div style={{ marginTop:12 }}>
              <label style={{ fontSize:11, fontWeight:700, color:T.slate, display:"block", marginBottom:6 }}>SESSION EFFECTIVENESS (your rating)</label>
              <div style={{ display:"flex", gap:8 }}>
                {[1,2,3,4,5].map(r=>(
                  <button key={r} onClick={()=>setForm(f=>({...f, teacherRating:r}))} style={{
                    width:36, height:36, borderRadius:8, border:`2px solid ${form.teacherRating>=r ? group.color : T.mist}`,
                    background: form.teacherRating>=r ? group.color : T.white,
                    color: form.teacherRating>=r ? T.white : T.slate,
                    cursor:"pointer", fontWeight:800, fontSize:14,
                  }}>{r}</button>
                ))}
                <span style={{ fontSize:11, color:T.slate, alignSelf:"center", marginLeft:4 }}>
                  {["","Poor","Fair","Good","Great","Excellent"][form.teacherRating]}
                </span>
              </div>
            </div>

            <div style={{ marginTop:16, display:"flex", gap:10 }}>
              <button onClick={handleAddSession} style={{
                background:group.color, color:T.white, border:"none",
                borderRadius:8, padding:"9px 20px", cursor:"pointer",
                fontSize:13, fontWeight:700,
              }}>Save Session</button>
              <button onClick={()=>setShowLogForm(false)} style={{
                background:T.bg, color:T.slate, border:`1px solid ${T.mist}`,
                borderRadius:8, padding:"9px 20px", cursor:"pointer", fontSize:13, fontWeight:600,
              }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Session rows */}
        {groupSessions.length === 0 ? (
          <div style={{ textAlign:"center", padding:"32px 20px", color:T.slate }}>
            <div style={{ fontSize:32, marginBottom:8 }}>📋</div>
            <div style={{ fontWeight:700 }}>No sessions logged yet</div>
            <div style={{ fontSize:12, marginTop:4 }}>Click "Log New Session" to record your first small group session</div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {[...groupSessions].reverse().map((ses, idx) => {
              const scores = Object.values(ses.exitTicketScores);
              const avg = scores.length ? Math.round(scores.reduce((a,b)=>a+b,0)/scores.length) : null;
              return (
                <div key={ses.id} style={{
                  background: T.bg, borderRadius:10, padding:"12px 16px",
                  border:`1px solid ${T.mist}`,
                  display:"flex", gap:14, alignItems:"flex-start", flexWrap:"wrap",
                }}>
                  <div style={{ minWidth:70, textAlign:"center" }}>
                    <div style={{ fontSize:10, color:T.slate, fontWeight:600 }}>
                      {new Date(ses.date).toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                    </div>
                    <div style={{
                      fontSize:22, fontWeight:900,
                      color: avg >= 65 ? T.teal : avg >= 50 ? T.amber : T.rose,
                      lineHeight:1.1,
                    }}>{avg ?? "—"}</div>
                    <div style={{ fontSize:9, color:T.slate }}>avg score</div>
                  </div>
                  <div style={{ flex:1, minWidth:200 }}>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:5 }}>
                      <Badge text={ses.skill} color={group.color}/>
                      {ses.subSkill && (
                        <span style={{
                          display:"inline-flex", alignItems:"center", gap:4,
                          background:T.teal+"15", border:`1px solid ${T.teal}44`,
                          color:T.teal, borderRadius:99, padding:"2px 9px",
                          fontSize:11, fontWeight:700,
                        }}>↳ {ses.subSkill}</span>
                      )}
                      <Badge text={`${ses.studentsPresent.length} students`} color={T.slate}/>
                      <Badge text={"⭐".repeat(ses.teacherRating)} color={T.amber}/>
                    </div>
                    <div style={{ fontSize:12, color:T.ink, lineHeight:1.5 }}>{ses.notes}</div>
                    <div style={{ display:"flex", gap:6, marginTop:6, flexWrap:"wrap" }}>
                      {ses.studentsPresent.map(sid => {
                        const stu = STUDENTS.find(s=>s.id===sid);
                        const sc  = ses.exitTicketScores[sid];
                        return (
                          <span key={sid} style={{
                            fontSize:11, background:T.white, borderRadius:6,
                            padding:"2px 8px", border:`1px solid ${T.mist}`,
                            color: sc >= 65 ? T.teal : sc >= 50 ? T.amber : T.rose,
                            fontWeight:600,
                          }}>
                            {stu?.name?.split(" ")[0]}: {sc ?? "—"}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SECTION: STUDENT DETAIL
// ─────────────────────────────────────────────────────────────

function StudentDetailSection({ groups, sessions, subject="ELA" }) {
  const [selected, setSelected] = useState(STUDENTS[0].id);
  const student = STUDENTS.find(s=>s.id===selected);
  const isMath = subject === "Math";

  // Subject-aware helpers
  const getScore  = s => isMath ? (s.math_score  ?? s.iready) : s.iready;
  const getGrowth = s => isMath ? (s.math_growth ?? s.growth) : s.growth;
  const getGaps   = s => isMath ? (s.math_gaps   ?? s.skill_gaps ?? []) : (s.ela_gaps ?? s.skill_gaps ?? []);
  const getStr    = s => isMath ? (s.math_strengths ?? s.strengths ?? []) : (s.ela_strengths ?? s.strengths ?? []);

  // Find which group this student is in
  const studentGroup = groups.find(g => g.students.find(s=>s.id===selected));
  const groupSessions = studentGroup ? (sessions[studentGroup.id]||[]) : [];

  // Build progress data
  const progressData = groupSessions
    .filter(ses=>ses.exitTicketScores[selected])
    .map((ses,idx)=>({
      session:`S${groupSessions.indexOf(ses)+1}`,
      score: ses.exitTicketScores[selected],
      date: ses.date,
      skill: ses.subSkill ? `${ses.skill} → ${ses.subSkill}` : ses.skill,
    }));

  // Projection
  let projectedData = [];
  if (progressData.length >= 2) {
    const scores = progressData.map(p=>p.score);
    const gain = (scores[scores.length-1]-scores[0])/(scores.length-1);
    const last = scores[scores.length-1];
    for (let w=1; w<=10; w++) {
      projectedData.push({
        session:`P${w}`,
        projected: Math.min(99, Math.round(last + gain * w * 0.8)),
      });
    }
  }

  const allData = [
    ...progressData.map(p=>({ ...p, projected:null })),
    ...projectedData.map(p=>({ ...p, score:null, date:null, skill:null })),
  ];

  const riskColor = s => s.iready < 460 ? T.rose : s.iready < 490 ? T.amber : T.teal;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <SectionTitle icon="👤" title={`${subject} · Individual Student View`}
        sub="Per-student progress monitoring · skill gap analysis · group placement · intervention trajectory"/>

      <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
        {/* Student list */}
        <div style={{ width:200, flexShrink:0 }}>
          <div style={{ fontSize:11, fontWeight:700, color:T.slate, marginBottom:8, textTransform:"uppercase", letterSpacing:".05em" }}>Select Student</div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {STUDENTS.map(s => {
              const grp = groups.find(g=>g.students.find(st=>st.id===s.id));
              return (
                <button key={s.id} onClick={()=>setSelected(s.id)} style={{
                  border:`2px solid ${selected===s.id ? (grp?.color||T.blue) : T.mist}`,
                  background: selected===s.id ? (grp?.color||T.blue)+"12" : T.white,
                  borderRadius:8, padding:"8px 12px", cursor:"pointer", textAlign:"left",
                  display:"flex", alignItems:"center", gap:8,
                }}>
                  <div style={{
                    width:36, height:36, borderRadius:10, flexShrink:0,
                    background:`linear-gradient(135deg, ${grp?.color||T.slate}BB, ${grp?.color||T.slate}77)`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:13, fontWeight:900, color:"white",
                  }}>
                    {s.name.split(" ").map(n=>n[0]).join("")}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:T.navy }}>{s.name}</div>
                    <div style={{ fontSize:10, color:T.slate }}>Gr{s.grade} · {grp?.tier||"Unassigned"}</div>
                    <div style={{ fontSize:10, color: (isMath?(s.math_score??s.iready):s.iready)>=490?T.teal:(isMath?(s.math_score??s.iready):s.iready)>=460?T.amber:T.rose, fontWeight:700 }}>{subject}: {isMath?(s.math_score??s.iready):s.iready}</div>
                    {(() => {
                      const auto = getAutoBadges(s);
                      const acts = s.activities || [];
                      const allIcons = [
                        ...auto.map(b => b.icon),
                        ...acts.map(a => (ACTIVITY_STYLES[a]||{icon:"📌"}).icon),
                      ];
                      return allIcons.length > 0 ? (
                        <div style={{ fontSize:10, color:T.slate, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {allIcons.slice(0,5).join(" ")}{allIcons.length > 5 ? ` +${allIcons.length-5}` : ""}
                        </div>
                      ) : null;
                    })()}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Student detail */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", gap:14 }}>
          {student && (
            <>
              {/* Header */}
              <Card style={{ background: studentGroup?.color+"08" || T.white, border:`1.5px solid ${studentGroup?.color||T.mist}44` }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
                  <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
                    {/* Large styled avatar */}
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                      <div style={{
                        width:72, height:72, borderRadius:18,
                        background:`linear-gradient(135deg, ${studentGroup?.color||T.blue}CC, ${studentGroup?.color||T.blue}88)`,
                        border:`3px solid ${studentGroup?.color||T.blue}44`,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:26, fontWeight:900, color:"white",
                        boxShadow:`0 4px 16px ${studentGroup?.color||T.blue}33`,
                        letterSpacing:"-1px", flexShrink:0,
                      }}>
                        {student.name.split(" ").map(n=>n[0]).join("")}
                      </div>
                      <div style={{ fontSize:10, color:T.slate, fontWeight:600 }}>Grade {student.grade}</div>
                    </div>
                    {/* Name, group, badges, activities */}
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:20, fontWeight:900, color:T.navy, lineHeight:1.2 }}>{student.name}</div>
                      <div style={{ fontSize:12, color:T.slate, marginTop:2 }}>
                        {studentGroup ? `${studentGroup.icon} ${studentGroup.tier} Group` : "Not yet grouped"}
                      </div>
                      <div style={{ display:"flex", gap:6, marginTop:6, flexWrap:"wrap" }}>
                        {student.iep && <Badge text="IEP" color={T.purple}/>}
                        {student.ell && <Badge text="ELL" color={T.sky}/>}
                        <Badge text={`Prior NYS L${student.nys_prior}`} color={[T.rose,T.rose,T.amber,T.teal,T.teal][student.nys_prior]}/>
                      </div>
                      {/* Auto badges — Honor Roll, Perfect Attendance, etc */}
                      {(() => {
                        const autoBadges = getAutoBadges(student);
                        return autoBadges.length > 0 ? (
                          <div style={{ marginTop:10 }}>
                            <div style={{ fontSize:10, fontWeight:700, color:T.slate, textTransform:"uppercase", letterSpacing:".05em", marginBottom:5 }}>
                              Achievements
                            </div>
                            <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                              {autoBadges.map((b,i) => (
                                <span key={i} title={b.tooltip} style={{
                                  display:"inline-flex", alignItems:"center", gap:4,
                                  background:b.color+"20", border:`2px solid ${b.color}55`,
                                  color:b.color, borderRadius:99, padding:"3px 11px",
                                  fontSize:11, fontWeight:800,
                                  boxShadow:`0 1px 4px ${b.color}22`,
                                }}>
                                  {b.icon} {b.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })()}

                      {/* Manual activities & programs */}
                      {student.activities && student.activities.length > 0 && (
                        <div style={{ marginTop:8 }}>
                          <div style={{ fontSize:10, fontWeight:700, color:T.slate, textTransform:"uppercase", letterSpacing:".05em", marginBottom:5 }}>
                            Programs & Activities
                          </div>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                            {student.activities.map((act,i) => {
                              const astyle = ACTIVITY_STYLES[act] || { color:T.slate, icon:"📌" };
                              return (
                                <span key={i} style={{
                                  display:"inline-flex", alignItems:"center", gap:4,
                                  background:astyle.color+"15", border:`1px solid ${astyle.color}44`,
                                  color:astyle.color, borderRadius:99, padding:"3px 10px",
                                  fontSize:11, fontWeight:700,
                                }}>
                                  {astyle.icon} {act}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                    {[
                      { label:`iReady ${subject}`, val:getScore(student), color:riskColor(student) },
                      { label:"Growth", val:`${getGrowth(student)>=0?"+":""}${getGrowth(student)}`, color:getGrowth(student)>=0?T.teal:T.rose },
                      { label:"Att %",  val:`${student.att}%`, color:student.att<90?T.rose:student.att<96?T.amber:T.teal },
                      { label:"Sim 3",  val:`${student.sim}%`, color:student.sim<50?T.rose:student.sim<65?T.amber:T.teal },
                    ].map(m=>(
                      <div key={m.label} style={{ textAlign:"center", background:T.white, borderRadius:10, padding:"8px 12px", border:`1px solid ${T.mist}`, minWidth:60 }}>
                        <div style={{ fontSize:10, color:T.slate, fontWeight:600 }}>{m.label}</div>
                        <div style={{ fontSize:18, fontWeight:900, color:m.color }}>{m.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Skill Gaps + Strengths */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                <Card>
                  <div style={{ fontSize:12, fontWeight:800, color:T.rose, marginBottom:8 }}>⚠ {subject} Skill Gaps — Focus Areas</div>
                  {getGaps(student).length ? getGaps(student).map((sk,i)=>(
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 0", borderBottom:`1px solid ${T.mist}` }}>
                      <span style={{ fontSize:12, color:T.rose }}>●</span>
                      <span style={{ fontSize:13, color:T.ink, fontWeight:500 }}>{sk}</span>
                      {i===0 && <Badge text="Priority" color={T.rose}/>}
                    </div>
                  )) : <div style={{ color:T.teal, fontWeight:600, fontSize:13 }}>✓ No significant {subject} skill gaps identified</div>}
                </Card>
                <Card>
                  <div style={{ fontSize:12, fontWeight:800, color:T.teal, marginBottom:8 }}>✓ {subject} Strengths — Leverage</div>
                  {getStr(student).length ? getStr(student).map((sk,i)=>(
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 0", borderBottom:`1px solid ${T.mist}` }}>
                      <span style={{ fontSize:12, color:T.teal }}>●</span>
                      <span style={{ fontSize:13, color:T.ink, fontWeight:500 }}>{sk}</span>
                    </div>
                  )) : <div style={{ color:T.slate, fontSize:13 }}>Strengths emerging — continue monitoring</div>}
                </Card>
              </div>

              {/* Progress chart */}
              {progressData.length > 0 ? (
                <Card>
                  <div style={{ fontSize:13, fontWeight:800, color:T.navy, marginBottom:4 }}>
                    Progress Monitoring — Exit Ticket Scores + Projection
                  </div>
                  <div style={{ fontSize:11, color:T.slate, marginBottom:14 }}>
                    Solid line = actual scores · Dashed = projected trajectory if growth rate maintained
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={allData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.mist}/>
                      <XAxis dataKey="session" tick={{fontSize:10,fill:T.slate}}/>
                      <YAxis domain={[20,100]} tick={{fontSize:10,fill:T.slate}}/>
                      <Tooltip contentStyle={{borderRadius:10,fontSize:12}}
                        formatter={(v,n,p)=>[v?`${v}%`:"—", n]}/>
                      <ReferenceLine y={65} stroke={T.blue} strokeDasharray="4 2"
                        label={{value:"Proficiency",fill:T.blue,fontSize:10}}/>
                      <Line type="monotone" dataKey="score" stroke={studentGroup?.color||T.blue}
                        strokeWidth={2.5} dot={{r:5}} name="Actual Score" connectNulls={false}/>
                      <Line type="monotone" dataKey="projected" stroke={T.slate}
                        strokeWidth={1.5} strokeDasharray="6 3" dot={{r:3}} name="Projected"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              ) : (
                <Card style={{ textAlign:"center", padding:"28px", color:T.slate }}>
                  <div style={{ fontSize:28 }}>📊</div>
                  <div style={{ fontWeight:700, marginTop:6 }}>No session data yet for {student.name}</div>
                  <div style={{ fontSize:12, marginTop:4 }}>Log sessions in the Progress Monitor tab to track this student's growth</div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────

const TABS = [
  { id:"groups",   label:"Smart Groups",       icon:"🧩" },
  { id:"trajectory",label:"Trajectory",        icon:"📈" },
  { id:"monitor",  label:"Progress Monitor",   icon:"📋" },
  { id:"student",  label:"Student View",       icon:"👤" },
];

// Teacher identity map — matches Auth.js user IDs to display info
const TEACHER_PROFILES = {
  TCH001: { name:"Ms. Rivera",   grades:"3-4", subject:"ELA",      color:"#00A878" },
  TCH002: { name:"Mr. Thompson", grades:"4-5", subject:"Math",     color:"#7C3AED" },
  TCH003: { name:"Ms. Patel",    grades:"5-6", subject:"ELA/Math", color:"#F59E0B" },
  TCH004: { name:"Mr. Brooks",   grades:"6-7", subject:"Math",     color:"#E03B3B" },
  TCH005: { name:"Ms. Kim",      grades:"6",   subject:"ELA",      color:"#0891B2" },
  TCH006: { name:"Mr. Torres",   grades:"7-8", subject:"Math",     color:"#059669" },
};

export default function TeacherPlatform({ currentUser, viewAsTeacherId, onBackToAdmin }) {
  const [activeTab, setActiveTab] = useState("groups");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [sessions, setSessions] = useState(INITIAL_SESSIONS);
  const [subject, setSubject] = useState("ELA");

  // If admin is viewing a specific teacher's data, use that teacher's profile
  // Otherwise use the logged-in teacher's own profile
  const effectiveTeacherId = viewAsTeacherId || currentUser?.id;
  const teacherProfile = TEACHER_PROFILES[effectiveTeacherId] || TEACHER_PROFILES["TCH001"];
  const isAdminViewing = currentUser?.role === "admin" && viewAsTeacherId;

  // Generate groups based on selected subject
  const groups = generateGroups(STUDENTS, subject);
  const mathStudents = STUDENTS.filter(s => s.math_score !== undefined);

  // Reset selected group when subject changes
  React.useEffect(() => {
    setSelectedGroup(null);
  }, [subject]);

  // School-wide KPIs
  const totalHigh = STUDENTS.filter(s=>s.iready<460||s.growth<0).length;
  const avgGrowth = (STUDENTS.reduce((a,s)=>a+s.growth,0)/STUDENTS.length).toFixed(1);
  const totalSessions = Object.values(sessions).reduce((a,v)=>a+v.length,0);

  return (
    <div style={{ fontFamily:"'DM Sans','Segoe UI',sans-serif", background:T.bg, minHeight:"100vh", color:T.navy }}>

      {/* Admin-viewing banner */}
      {isAdminViewing && (
        <div style={{
          background:`linear-gradient(90deg, #1A2E4A, #0D1B2A)`,
          color:"#A3C4E8", padding:"8px 24px",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          fontSize:12, borderBottom:"2px solid #1A6EBD",
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:14 }}>🔍</span>
            <strong style={{ color:"#FFFFFF" }}>Admin View:</strong>
            &nbsp;You are viewing {teacherProfile.name}'s classroom as {currentUser?.name}
          </div>
          {onBackToAdmin && (
            <button onClick={onBackToAdmin} style={{
              background:"#1A6EBD", border:"none", borderRadius:7, padding:"5px 14px",
              color:"#FFFFFF", cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"inherit",
            }}>
              ← Back to Admin Dashboard
            </button>
          )}
        </div>
      )}

      {/* Header */}
      <div style={{
        background:T.navy, color:T.white, height:56,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"0 24px", position:"sticky", top:0, zIndex:50,
        boxShadow:"0 2px 20px rgba(0,0,0,.2)",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{
            width:32, height:32, borderRadius:8,
            background:`linear-gradient(135deg, ${T.sky}, ${T.teal})`,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontWeight:900, fontSize:14,
          }}>T</div>
          <div>
            <span style={{ fontWeight:900, fontSize:15 }}>EduMetrics</span>
            <span style={{ color:T.sky, fontWeight:900, fontSize:15 }}> · Teacher Studio</span>
            <div style={{ fontSize:9, color:"#7A93B0", letterSpacing:".08em" }}>SMALL GROUP INTELLIGENCE · PROGRESS MONITORING</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:12, alignItems:"center" }}>
          <div style={{ fontSize:12, color:"#A3C4E8" }}>
            {teacherProfile.name} · {teacherProfile.subject} Gr {teacherProfile.grades} · {STUDENTS.length} students
          </div>
          {currentUser?.role === "admin" && !isAdminViewing && (
            <div style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:7, padding:"4px 12px", fontSize:11, color:"#7A93B0" }}>
              🔒 Admin Mode
            </div>
          )}
        </div>
      </div>

      {/* Subject switcher + KPI strip */}
      <div style={{ background:T.white, borderBottom:`1px solid ${T.mist}`, padding:"10px 24px" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          {/* ELA / Math toggle */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
            <span style={{ fontSize:12, fontWeight:700, color:T.slate }}>Subject:</span>
            {["ELA","Math"].map(s=>(
              <button key={s} onClick={()=>setSubject(s)} style={{
                padding:"7px 22px", borderRadius:99, border:"none", cursor:"pointer",
                fontFamily:"inherit", fontSize:13, fontWeight:800,
                background: subject===s
                  ? (s==="ELA" ? T.blue : T.teal)
                  : T.mist,
                color: subject===s ? "white" : T.slate,
                boxShadow: subject===s ? `0 2px 8px ${s==="ELA"?T.blue:T.teal}44` : "none",
                transition:"all .15s",
              }}>
                {s==="ELA" ? "📖 ELA" : "🔢 Math"}
              </button>
            ))}
            <span style={{ fontSize:11, color:T.slate, marginLeft:8 }}>
              All four tabs update when you switch subjects
            </span>
          </div>
          {/* KPIs */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12 }}>
            <KPI label="My Students"      value={STUDENTS.length}  sub="active this year"          color={T.blue}   icon="👥"/>
            <KPI label="Groups Generated" value={groups.length}    sub={`${subject} auto groups`}  color={T.teal}   icon="🧩"/>
            <KPI label="Need Intensive"   value={totalHigh}        sub="daily sessions recommended" color={T.rose}   icon="🔴"/>
            <KPI label="Avg iReady Growth" value={`${avgGrowth>=0?"+":""}${avgGrowth}`} sub={`${subject} scale score pts`} color={avgGrowth>5?T.teal:T.amber} icon="📈"/>
            <KPI label="Sessions Logged"  value={totalSessions}    sub="across all groups"          color={T.purple} icon="📋"/>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ background:T.white, borderBottom:`1px solid ${T.mist}`, padding:"0 24px", display:"flex", gap:0 }}>
        {TABS.map(tab=>(
          <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={{
            border:"none", background:"none", cursor:"pointer",
            padding:"13px 18px", display:"flex", alignItems:"center", gap:6,
            fontSize:13, fontWeight:activeTab===tab.id?800:500,
            color:activeTab===tab.id?T.blue:T.slate,
            borderBottom:activeTab===tab.id?`3px solid ${T.blue}`:"3px solid transparent",
            whiteSpace:"nowrap", transition:"all .15s",
          }}>
            <span style={{ fontSize:15 }}>{tab.icon}</span>{tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding:"24px", maxWidth:1200, margin:"0 auto" }}>
        {activeTab === "groups"    && <SmartGroupsSection groups={groups} onSelectGroup={setSelectedGroup} selectedGroupId={selectedGroup} subject={subject}/>}
        {activeTab === "trajectory"&& <TrajectorySection groups={groups} subject={subject}/>}
        {activeTab === "monitor"   && <ProgressMonitorSection groups={groups} sessions={sessions} setSessions={setSessions} subject={subject}/>}
        {activeTab === "student"   && <StudentDetailSection groups={groups} sessions={sessions} subject={subject}/>}
      </div>
    </div>
  );
}
